import { FeatureFrequencyQuery, FeatureFrequencyQueryVariables } from '@/graphql/generated/graphql';
import { FEATURE_FREQUENCY } from '@/graphql/queries/report/ga-interactions';
import { useQuery } from '@apollo/client';
import { ChartOptions } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useDashboardStore } from '../../store/useDashboardStore';
import { useEffect, useState } from 'react';
import useTranslation from '@/hooks/i18n';
import { getColor } from '../../utils/colors';
import { useAdminStore } from '@/pages/admin/store/useAdmin.store';

interface DataType {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor: string;
  }[];
}

export default function FeatureUsage() {
  const { page } = useAdminStore();

  const { getDateRange, schoolsIds, getPeriod } = useDashboardStore();
  const { t } = useTranslation();
  const [total, setTotal] = useState<string>('0');
  const [data, setData] = useState<DataType>({
    labels: [],
    datasets: [],
  });

  const { data: response, loading } = useQuery<FeatureFrequencyQuery, FeatureFrequencyQueryVariables>(
    FEATURE_FREQUENCY,
    {
      variables: {
        startDate: getDateRange()?.startDate,
        endDate: getDateRange()?.endDate,
        schoolsIds,
        period: getPeriod(),
      },
    },
  );

  const options: ChartOptions<'bar'> = {
    responsive: true,
    scales: {
      x: {
        stacked: true,
        title: {
          display: true,
          text: 'Date',
        },
      },
      y: {
        stacked: true,
        title: {
          display: true,
          text: 'Time of usage in minutes',
        },
        ticks: {
          callback: (value) => `${value} min`,
        },
      },
    },
    plugins: {
      legend: {
        position: 'bottom',
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const value = tooltipItem.raw as number;
            const label = data.labels[tooltipItem.dataIndex];
            const feature = tooltipItem.dataset.label;
            return `${label}, ${feature}: ${value.toFixed(2)} min`;
          },
        },
      },
    },
  };

  useEffect(() => {
    if (response && response.getFeatureFrequency) {
      const labels = [
        ...new Set<string>(response.getFeatureFrequency.map((user) => `${user!.date!} (${user!.schoolName!})`)),
      ];

      const features = [
        ...new Set<string>(
          response.getFeatureFrequency
            .map((e) => e!.features!)
            .flatMap((e) => e)
            .map((e) => e?.feature || ''),
        ),
      ];

      const datasets: { label: string; data: number[]; backgroundColor: string }[] = [];
      let total = 0;

      features.forEach((feature, featureIndex) => {
        const dataset = {
          label: t(feature === 'null' ? 'Others' : feature),
          data: [] as number[],
          backgroundColor: getColor(featureIndex),
        };

        dataset.data = labels.map((label) => {
          const [date, schoolName] = label.split(' (');
          const cleanSchoolName = schoolName.replace(')', '');

          const item =
            response.getFeatureFrequency
              ?.find((user) => user?.date === date && user.schoolName === cleanSchoolName)
              ?.features?.find((e) => e?.feature === feature)?.averageUseInSeconds || 0;

          const minutes = item / 60;
          total += item;

          return minutes;
        });

        datasets.push(dataset);
      });

      setData({
        labels: labels,
        datasets,
      });

      setTotal(formatSecondsToTime(total));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  function formatSecondsToTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(remainingSeconds).padStart(2, '0');

    return `${hours}:${formattedMinutes}:${formattedSeconds}`;
  }

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center">
          <div className="h-64 w-full animate-pulse rounded-sm bg-gray-300"></div>
        </div>
      );
    }

    if (data.labels.length === 0) {
      return (
        <div className="flex items-center justify-center">
          {/* //Empty state of a doughnut chart */}
          <div className="flex flex-col items-center justify-center">
            <p className="text-sh-xs text-gray-500">No data available</p>
          </div>
        </div>
      );
    }
    return <Bar data={data} options={options} style={{ width: 'auto' }} />;
  };
  return (
    <>
      <div className="border-b-2 border-[#ECECEC] px-11 py-6">
        <p className=" text-sh-xs font-bold">{page?.dashboard?.chartsTitles?.featureUsage || 'Feature Usage'}</p>
        <p className="text-sh-36-48 text-[#0B1354]">{total || '-'}</p>
        <p className=" text-[#4F4F4F]">Total minutes</p>
      </div>
      <div className=" px-sh-30 py-sh-15">{renderContent()}</div>
    </>
  );
}
